import { memo, useEffect, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { cn } from "@racoon/ui";
import {
  ConstructionKamaz,
  DefaultTruck,
  DoubleJob,
  IsuzuElf,
  IsuzuGiga,
  Kmu,
  Samosval,
  TrashKamaz,
} from "~/shared/assets/images";
import { getTruckImageUrl } from "~/shared/lib/truck-images";

/**
 * Local render used when the truck has no S3 photo for its
 * type/model/year/color combination.
 */
const fallbackImage = (type?: ConnectProto.TruckType, model?: string) => {
  if (type === ConnectProto.TruckType.DOUBLE_CONTAINER) return DoubleJob;
  if (type === ConnectProto.TruckType.MSW) return TrashKamaz;
  if (type === ConnectProto.TruckType.LOADER_CRANE) return Kmu;
  if (type === ConnectProto.TruckType.TIPPER) return Samosval;
  const modelLower = model?.toLocaleLowerCase() || "";
  if (modelLower.includes("камаз")) return ConstructionKamaz;

  switch (modelLower) {
    case "giga":
      return IsuzuGiga;
    case "elf":
      return IsuzuElf;
    default:
      return DefaultTruck;
  }
};

type TruckImageProps = {
  /** Partial so a form can preview an unsaved type/year/colour combination. */
  truck?: Partial<ConnectProto.Truck>;
  /** Dims the render — used for trucks with no active shift. */
  grayscale?: boolean;
  className?: string;
};

/**
 * Truck photo shown across the app (autopark cards, shift schedule popover):
 * the S3 render for the exact truck, falling back to a bundled image when that
 * file is missing.
 */
export const TruckImage = memo<TruckImageProps>(({ truck, grayscale, className }) => {
  const type = truck?.truckType?.type || truck?.type;
  const model = truck?.truckType?.model || truck?.model;
  const remoteUrl = getTruckImageUrl(type, model, Number(truck?.year), truck?.color);

  const [remoteFailed, setRemoteFailed] = useState(false);
  useEffect(() => setRemoteFailed(false), [remoteUrl]);

  const useRemote = !!remoteUrl && !remoteFailed;

  return (
    <img
      src={useRemote ? remoteUrl : fallbackImage(type, model)}
      alt={model || ""}
      onError={useRemote ? () => setRemoteFailed(true) : undefined}
      className={cn("max-h-full max-w-full object-contain", grayscale && "grayscale", className)}
    />
  );
});

TruckImage.displayName = "TruckImage";
